import { duplicateNodeAtCursor } from "@/components/input/utils/duplicateNodeAtCursor";
import { writeSliceToClipboard } from "@/components/tiptap/copy-paste/writeSliceToClipboard";
import { deleteSelection } from "@/components/tiptap/utils/deleteSelection";
import { duplicateSelection } from "@/components/tiptap/utils/duplicateTiptapLine";

import type { ClipboardCommand } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorView } from "@tiptap/pm/view";

/** Commands that act on the right-clicked node and leave the caret where it is. */
const AT_CURSOR_COMMANDS: ClipboardCommand[] = ["duplicate-at-cursor", "reference-at-cursor"];

export function executeClipboardCommand(command: ClipboardCommand, doc: DocumentModel): void {
  const view = doc.editor.tiptap?.view;

  if (!AT_CURSOR_COMMANDS.includes(command)) {
    selectPointerNode(doc);
  }

  switch (command) {
    case "copy":
      if (view) copySelection(view);
      return;
    case "cut":
      if (view) cutSelection(doc, view);
      return;
    case "duplicate":
      duplicateSelection(doc);
      return;
    case "duplicate-at-cursor":
    case "reference-at-cursor": {
      const { pointerNode } = doc.editor.suggestions;
      if (pointerNode) duplicateNodeAtCursor(doc, pointerNode);
      return;
    }
    case "paste":
      if (view) pasteFromClipboard(view);
      return;
  }
}

function selectPointerNode(doc: DocumentModel): void {
  const node = doc.editor.suggestions.pointerNode;
  if (!node || doc.selection.hasActionableSelection) return;

  if (!doc.cursor.isActive) {
    doc.cursor.moveBeforeNode(node, { resetSelection: false });
  }
  doc.selection.selectNode(node);
}

async function copySelection(view: EditorView): Promise<void> {
  await writeSelectionToClipboard(view);
}

async function cutSelection(doc: DocumentModel, view: EditorView): Promise<void> {
  const copied = await writeSelectionToClipboard(view);
  if (!copied) return;

  doc.transact(() => {
    deleteSelection(doc, view);
    return true;
  }, "command:cut");
}

function writeSelectionToClipboard(view: EditorView): Promise<boolean> {
  return writeSliceToClipboard(view, view.state.selection.content());
}

async function pasteFromClipboard(view: EditorView): Promise<void> {
  try {
    if (navigator.clipboard?.read) {
      const items = await navigator.clipboard.read();
      const htmlItem = items.find((item) => item.types.includes("text/html"));
      if (htmlItem) {
        const blob = await htmlItem.getType("text/html");
        view.pasteHTML(await blob.text());
        return;
      }
    }

    const text = await navigator.clipboard?.readText?.();
    if (text) {
      view.pasteText(text);
    }
  } catch {}
}
